import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { Handshake, Award, Lightbulb, Users, ArrowRight, CheckCircle } from 'lucide-react';
import { useT } from '../context/LanguageContext';
import { ui, teamData, valuesData } from '../translations';

const ICON_MAP = { Handshake, Award, Lightbulb, Users };

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('visible'); obs.unobserve(el); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
};

const AboutPage = () => {
  const t = useT();
  const lang = t({ ro: 'ro', en: 'en' });
  useSEO({
    title: lang === 'ro'
      ? 'Despre Noi | Bee-Advised – Experți IT & Securitate România'
      : 'About Us | Bee-Advised – IT & Security Experts Romania',
    description: lang === 'ro'
      ? 'Bee-Advised – echipă de specialiști certificați cu peste 10 ani experiență în IT, securitate cibernetică, CCTV, smart home și BMS în România.'
      : 'Bee-Advised – certified specialists team with 10+ years experience in IT, cybersecurity, CCTV, smart home and BMS in Romania.',
    canonical: 'https://www.bee-advised.ro/despre',
    lang,
  });
  const r1 = useReveal(), r2 = useReveal(), r3 = useReveal(), r4 = useReveal();

  const stats = [
    { value: '10+', label: t({ ro: 'Ani de Experiență', en: 'Years of Experience' }) },
    { value: '200+', label: t({ ro: 'Proiecte Finalizate', en: 'Projects Completed' }) },
    { value: '150+', label: t({ ro: 'Clienți Mulțumiți', en: 'Satisfied Clients' }) },
    { value: '13', label: t({ ro: 'Servicii Oferite', en: 'Services Offered' }) },
  ];

  return (
    <div>
      {/* PAGE HERO */}
      <section data-testid="about-hero" className="relative bg-[#0A192F] hex-pattern pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-badge"><span className="w-8 h-px bg-[#00BCD4]"/>{t(ui.about.badge)}</div>
          <h1 className="text-5xl md:text-6xl font-black text-white font-heading tracking-tight leading-none mt-3">
            {t(ui.about.heroTitle)}
          </h1>
          <p className="text-slate-400 mt-4 text-lg max-w-2xl leading-relaxed">{t(ui.about.heroSub)}</p>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-100">
            {stats.map((s, i) => (
              <div key={i} className="text-center py-10 px-4">
                <div className="text-4xl font-black text-[#0A192F] font-heading">{s.value}</div>
                <div className="text-slate-500 text-sm mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STORY */}
      <section data-testid="about-story" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div ref={r1} className="reveal-left">
              <div className="section-badge"><span className="w-8 h-px bg-[#00BCD4]"/>{t(ui.about.storyBadge)}</div>
              <h2 className="text-4xl md:text-5xl font-black text-[#0A192F] font-heading tracking-tight mt-2">{t(ui.about.storyTitle)}</h2>
              <p className="text-slate-600 mt-6 text-base leading-relaxed">{t(ui.about.storyP1)}</p>
              <p className="text-slate-600 mt-4 text-base leading-relaxed">{t(ui.about.storyP2)}</p>
              <ul className="mt-8 space-y-3">
                {[
                  t({ ro: 'Certificări internaționale IT & Securitate', en: 'International IT & Security certifications' }),
                  t({ ro: 'Experiență în proiecte rezidențiale și corporative', en: 'Experience in residential and corporate projects' }),
                  t({ ro: 'Suport tehnic dedicat post-instalare', en: 'Dedicated post-installation technical support' }),
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700 text-sm">
                    <CheckCircle size={18} className="text-[#00BCD4] shrink-0"/>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div ref={r2} className="reveal relative">
              <div className="relative overflow-hidden" style={{ height: '450px' }}>
                <img src="https://images.unsplash.com/photo-1599949104055-2d04026aee1e?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Bee-Advised team" className="w-full h-full object-cover"/>
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0A192F]/30 to-transparent"/>
              </div>
              {/* Float badge */}
              <div className="absolute -bottom-6 -left-6 bg-[#0A192F] p-6 text-white">
                <div className="text-3xl font-black text-[#00BCD4]">10+</div>
                <div className="text-xs mt-1 uppercase tracking-wider">{t({ ro: 'Ani de Excelență', en: 'Years of Excellence' })}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-20 bg-[#0A192F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="section-badge justify-center"><span className="w-8 h-px bg-[#00BCD4]"/>{t(ui.about.missionBadge)}</div>
          <p className="text-2xl md:text-3xl font-bold text-white font-heading leading-relaxed mt-4">
            "{t(ui.about.missionText)}"
          </p>
        </div>
      </section>

      {/* VALUES */}
      <section data-testid="about-values" className="py-24 md:py-32 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={r3} className="reveal text-center mb-14">
            <div className="section-badge justify-center"><span className="w-8 h-px bg-[#00BCD4]"/>{t(ui.about.valuesBadge)}</div>
            <h2 className="text-4xl md:text-5xl font-black text-[#0A192F] font-heading tracking-tight mt-2">{t(ui.about.valuesTitle)}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {valuesData.map((v, i) => {
              const Icon = ICON_MAP[v.icon] || Award;
              return (
                <div key={i} data-testid={`value-card-${i}`} className="bg-white p-8 border border-slate-200 hover:border-[#00BCD4] hover:-translate-y-1 transition-all duration-300">
                  <div className="icon-container mb-5">
                    <Icon size={24} className="text-[#00BCD4]" strokeWidth={1.5}/>
                  </div>
                  <h3 className="font-bold text-[#0A192F] font-heading mb-2">{t(v.title)}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{t(v.desc)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section data-testid="about-team" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={r4} className="reveal text-center mb-14">
            <div className="section-badge justify-center"><span className="w-8 h-px bg-[#00BCD4]"/>{t(ui.about.teamBadge)}</div>
            <h2 className="text-4xl md:text-5xl font-black text-[#0A192F] font-heading tracking-tight mt-2">{t(ui.about.teamTitle)}</h2>
            <p className="text-slate-500 mt-4 max-w-xl mx-auto">{t(ui.about.teamSub)}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {teamData.map((m, i) => (
              <div key={i} data-testid={`team-member-${i}`} className="text-center group">
                <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center text-2xl font-black text-[#00BCD4] font-heading border-2 border-[#00BCD4]/30 group-hover:border-[#00BCD4] bg-[#0A192F] transition-all duration-300">
                  {m.initials}
                </div>
                <h3 className="font-bold text-[#0A192F] font-heading">{m.name}</h3>
                <p className="text-slate-500 text-sm mt-1">{t(m.role)}</p>
                <p className="text-[#00BCD4] text-xs mt-2 font-mono">{m.cert}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#F8FAFC] border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-black text-[#0A192F] font-heading">{t({ ro: 'Lucrăm împreună?', en: "Shall we work together?" })}</h2>
          <p className="text-slate-500 mt-3 mb-8">{t({ ro: 'Contactați-ne pentru o evaluare gratuită a nevoilor dvs.', en: 'Contact us for a free assessment of your needs.' })}</p>
          <Link to="/contact" className="btn-primary">{t({ ro: 'Contactați-ne', en: 'Contact Us' })} <ArrowRight size={16}/></Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
